import { useMemo, useState } from 'react'
import { Copy, Eraser, Sparkles } from 'lucide-react'
import { estimateTicket, isPleno, jaSingleColumn, ticketToText } from '@/lib/quiniela'
import type { GoalCat, QuinielaCurrent, QuinielaMatchView, QuinielaTicket, Sign } from '@/types/quiniela'
import { MatchProbRow } from './MatchProbRow'
import { PlenoCard } from './PlenoCard'
import { TicketSummary } from './TicketSummary'
import { PlayTicketAction } from './PlayTicket'

/** Créala tú mismo: el usuario marca 1/X/2 (uno, dos o tres signos por partido = simple/doble/triple). Mientras
 * lo arma se recalculan apuestas, coste y probabilidad del boleto con las probabilidades de JA. JA solo analiza:
 * los signos los decide el usuario. */
export function TicketBuilder({ current, matches }: { current: QuinielaCurrent; matches: QuinielaMatchView[] }) {
  const [picks, setPicks] = useState<Record<number, Sign[]>>({})
  const [pleno, setPleno] = useState<{ home: GoalCat; away: GoalCat } | null>(null)
  const [copied, setCopied] = useState(false)
  const [played, setPlayed] = useState<QuinielaTicket | null>(null)
  const price = current.rules.column_price_eur
  const regular = matches.filter((m) => m.position <= 14)
  const snapshotPleno = current.snapshot?.pleno
  const est = useMemo(() => estimateTicket(matches, picks, price), [matches, picks, price])
  const single = useMemo(() => estimateTicket(matches, jaSingleColumn(matches), price).probability, [matches, price])
  const filled = regular.filter((m) => (picks[m.position]?.length ?? 0) > 0).length
  const minCost = current.rules.min_cost_eur
  const belowMinimum = est.complete && est.columns < current.rules.min_columns
  const vsSingle = est.probability && single ? est.probability / single : null

  const toggle = (pos: number, sign: Sign) => {
    setPlayed(null)
    setPicks((prev) => {
      const cur = prev[pos] ?? []
      const next = cur.includes(sign) ? cur.filter((s) => s !== sign) : [...cur, sign]
      const order: Sign[] = ['1', 'X', '2']
      return { ...prev, [pos]: order.filter((s) => next.includes(s)) }
    })
  }

  const fillWithJA = () => {
    setPlayed(null)
    setPicks(jaSingleColumn(matches))
    if (isPleno(snapshotPleno)) setPleno({ home: snapshotPleno.home, away: snapshotPleno.away })
  }

  const effectivePleno = pleno ?? (isPleno(snapshotPleno) ? { home: snapshotPleno.home, away: snapshotPleno.away } : null)
  const ready = est.complete && est.columns >= current.rules.min_columns && !!effectivePleno && !!current.snapshot
  const playPayload = ready && current.snapshot && effectivePleno
    ? {
        origin: 'manual' as const, snapshot_id: current.snapshot.id, pleno: effectivePleno,
        selections: regular.map((m) => ({ position: m.position, signs: picks[m.position] })),
      }
    : null
  const playSummary = playPayload
    ? { columns: est.columns, cost: est.cost, dobles: est.dobles, triples: est.triples, selections: playPayload.selections, pleno: effectivePleno }
    : null
  const blocked = !est.complete ? 'completa los 14' : est.columns < current.rules.min_columns ? `mínimo ${current.rules.min_columns} apuestas` : !effectivePleno ? 'elige tu Pleno' : null

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(ticketToText(matches, picks, pleno ?? (isPleno(snapshotPleno) ? snapshotPleno : null)))
      setCopied(true)
      setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  const legend = (
    <div className="flex items-center justify-between pb-1">
      <div className="flex items-center gap-3 text-[10px] font-semibold text-text-dim">
        <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-brand" /> Simple</span>
        <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-cyan" /> Doble</span>
        <span className="inline-flex items-center gap-1"><i className="h-2 w-2 rounded-sm bg-violet" /> Triple</span>
      </div>
      <span className="figure text-[11px] text-text-dim">{filled}/14</span>
    </div>
  )

  return (
    <div className="flex flex-col gap-4 md:grid md:grid-cols-[minmax(0,1fr)_22rem] md:items-start md:gap-5">
      <section className="card order-2 md:order-none">
        {legend}
        {regular.map((m) => (
          <MatchProbRow key={m.position} match={m} selected={picks[m.position] ?? []} onToggle={(s) => toggle(m.position, s)} />
        ))}
      </section>

      <div className="contents md:block md:space-y-4 md:sticky md:top-20">
        <section className="card order-1 space-y-2.5">
          <p className="eyebrow text-brand">Tu boleto</p>
          <p className="text-[12px] text-text-muted leading-snug">
            Toca los signos que quieras jugar: uno es simple, dos doble y tres triple. JA calcula apuestas, coste y probabilidad.
          </p>
          <div className="flex items-center gap-2">
            <button type="button" onClick={fillWithJA} className="btn-secondary-app !py-2 flex-1">
              <Sparkles className="h-3.5 w-3.5 text-brand" /> Partir de la columna de JA
            </button>
            <button type="button" onClick={() => { setPlayed(null); setPicks({}); setPleno(null) }} className="btn-secondary-app !py-2" aria-label="Borrar selección">
              <Eraser className="h-3.5 w-3.5" />
            </button>
          </div>
        </section>

        <div className="order-3">
          <PlenoCard
            match={matches.find((m) => m.position === 15)} pleno={snapshotPleno}
            value={pleno} onChange={(v) => { setPlayed(null); setPleno(v) }} title="Tu Pleno al 15"
            note="Elige los goles de cada equipo. M = 3 o más. El porcentaje es el de JA."
          />
        </div>

        <div className="order-4 sticky bottom-[4.9rem] z-10 md:static">
          <div className="rounded-2xl border border-border bg-panel/95 backdrop-blur px-3 py-2.5 flex items-center justify-between gap-3 shadow-[0_-6px_20px_rgba(0,0,0,0.5)] md:shadow-none">
            <div className="min-w-0">
              <p className="text-[10px] text-text-dim">
                <span className="figure text-text-muted">{est.columns}</span> apuestas{!est.complete && ` · faltan ${14 - filled} partidos`}
              </p>
              <p className="figure text-sm font-black text-text">{est.cost.toFixed(2).replace('.', ',')} €</p>
            </div>
            <div className="w-[9.5rem] shrink-0">
              <PlayTicketAction
                current={current} payload={playPayload} summary={playSummary}
                played={played ? { columns: played.columns, cost_eur: played.cost_eur } : null}
                blockedReason={blocked} onPlayed={setPlayed}
              />
            </div>
          </div>
        </div>

        <div className="order-5 space-y-4">
          {est.complete ? (
            <>
              {belowMinimum && (
                <p className="card text-[11px] text-warn leading-snug" role="alert">
                  El boleto oficial exige al menos {current.rules.min_columns} apuestas ({minCost.toFixed(2).replace('.', ',')} €). Añade un doble para llegar al mínimo.
                </p>
              )}
              {est.missingData.length > 0 && (
                <p className="card text-[11px] text-warn leading-snug">
                  No podemos estimar la probabilidad: JA aún no tiene datos del partido {est.missingData.join(', ')}. Marca los tres signos si quieres cubrirlo.
                </p>
              )}
              <TicketSummary
                columns={est.columns} cost={est.cost} dobles={est.dobles} triples={est.triples}
                probability={est.probability} perMatchCoverage={est.perMatchCoverage} vsSingle={vsSingle}
              />
              <button type="button" onClick={copy} className="btn-secondary-app !py-2 w-full">
                <Copy className="h-3.5 w-3.5" /> {copied ? 'Copiado' : 'Copiar boleto'}
              </button>
            </>
          ) : (
            <p className="text-[11px] text-text-dim px-1">Completa los 14 partidos para ver el coste y la probabilidad de tu boleto.</p>
          )}
        </div>
      </div>
    </div>
  )
}
